/* Uebernommen aus der Content-Factory (components/loading-animation/revision-spinner.tsx), Auftrag
   2026-10-13. Inline-SVG mit eigenen Keyframes, Farben aus EDS-Tokens. */
import type { CSSProperties } from 'react';

type SpinnerProps = {
  speed?: number;
  className?: string;
  style?: CSSProperties;
  label?: string;
};

const KEYFRAMES = `
@keyframes cfType { 0%,100% { transform: rotate(-7deg); } 50% { transform: rotate(9deg); } }
@keyframes cfHeadJitter { 0%,100% { transform: translate(0,0) rotate(-1.5deg); } 25% { transform: translate(1px,1px) rotate(1.5deg); } 60% { transform: translate(-1px,0) rotate(0.5deg); } }
@keyframes cfMouth { 0%,100% { transform: scaleX(1); } 50% { transform: scaleX(0.72); } }
@keyframes cfSweat { 0% { transform: translate(0,0) scale(0.6); opacity: 0; } 18% { opacity: 1; transform: translate(0,2px) scale(1); } 100% { transform: translate(-6px,34px) scale(0.8); opacity: 0; } }
@keyframes cfSweatR { 0% { transform: translate(0,0) scale(0.6); opacity: 0; } 18% { opacity: 1; transform: translate(0,2px) scale(1); } 100% { transform: translate(7px,32px) scale(0.8); opacity: 0; } }
@keyframes cfSheet { 0% { transform: translate(0,0) rotate(-4deg); opacity: 0; } 12% { opacity: 1; } 55% { transform: translate(38px,-26px) rotate(16deg); opacity: 1; } 92% { transform: translate(74px,6px) rotate(-6deg); opacity: 1; } 100% { transform: translate(74px,10px) rotate(-6deg); opacity: 0; } }
@keyframes cfStack { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-1.5px); } }
@keyframes cfKey { 0%,100% { opacity: 0.25; } 40% { opacity: 1; } }
@keyframes cfZip { 0%,100% { opacity: 0; transform: translateX(0); } 50% { opacity: 1; transform: translateX(-4px); } }
@media (prefers-reduced-motion: reduce) { [data-cf-spin] * { animation-play-state: paused !important; } }
`;

export function RevisionSpinner({ speed = 1, className, style, label }: SpinnerProps) {
  const a = (
    name: string,
    dur: number,
    ease: string,
    extra: CSSProperties = {},
  ): CSSProperties => ({
    animation: `${name} ${dur / speed}s ${ease} infinite`,
    ...extra,
  });
  const box = (origin: string): CSSProperties => ({
    transformBox: 'fill-box',
    transformOrigin: origin,
  });
  const d = (s: number) => `${s / speed}s`;
  const keyDelays = [0, -0.14, -0.07, -0.21, 0];

  return (
    <svg
      data-cf-spin="text"
      viewBox="0 0 240 170"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={className}
      style={{ width: '100%', maxWidth: '15.75rem', height: 'auto', overflow: 'visible', ...style }}
    >
      <style>{KEYFRAMES}</style>
      <g style={a('cfStack', 1.1, 'ease-in-out', box('50% 100%'))}>
        <rect
          x="184"
          y="112"
          width="40"
          height="7"
          rx="2"
          fill="var(--eds-semantic-color-background-primary)"
          stroke="var(--eds-semantic-color-gray-300)"
          strokeWidth="2"
        />
        <rect
          x="186"
          y="104"
          width="40"
          height="7"
          rx="2"
          fill="var(--eds-semantic-color-background-primary)"
          stroke="var(--eds-semantic-color-gray-300)"
          strokeWidth="2"
        />
        <rect
          x="183"
          y="96"
          width="40"
          height="7"
          rx="2"
          fill="var(--eds-semantic-color-background-primary)"
          stroke="var(--eds-semantic-color-gray-300)"
          strokeWidth="2"
        />
      </g>
      <g style={a('cfSheet', 1.9, 'cubic-bezier(0.4,0,0.2,1)', box('50% 50%'))}>
        <rect
          x="140"
          y="82"
          width="30"
          height="36"
          rx="3"
          fill="var(--eds-semantic-color-background-primary)"
          stroke="var(--eds-semantic-color-gray-300)"
          strokeWidth="2"
        />
        <rect
          x="146"
          y="90"
          width="18"
          height="2.5"
          rx="1.25"
          fill="var(--eds-semantic-color-gray-300)"
        />
        <rect
          x="146"
          y="97"
          width="18"
          height="2.5"
          rx="1.25"
          fill="var(--eds-semantic-color-gray-300)"
        />
        <rect
          x="146"
          y="104"
          width="11"
          height="2.5"
          rx="1.25"
          fill="var(--eds-semantic-color-accent-primary-800)"
        />
      </g>
      <rect
        x="16"
        y="130"
        width="208"
        height="7"
        rx="3.5"
        fill="var(--eds-semantic-color-gray-900)"
      />
      <g style={a('cfHeadJitter', 0.42, 'ease-in-out', box('50% 100%'))}>
        <rect
          x="100"
          y="30"
          width="44"
          height="38"
          rx="11"
          fill="var(--eds-semantic-color-background-primary)"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="3"
        />
        <rect
          x="107"
          y="24"
          width="30"
          height="10"
          rx="5"
          fill="var(--eds-semantic-color-gray-900)"
        />
        <circle cx="113" cy="48" r="3" fill="var(--eds-semantic-color-gray-900)" />
        <circle cx="131" cy="48" r="3" fill="var(--eds-semantic-color-gray-900)" />
        <path
          d="M107 41 L117 43"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          d="M137 41 L127 43"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <rect
          x="113"
          y="57"
          width="18"
          height="3.5"
          rx="1.75"
          fill="var(--eds-semantic-color-gray-900)"
          style={a('cfMouth', 0.84, 'ease-in-out', box('50% 50%'))}
        />
      </g>
      <g style={a('cfSweat', 1.3, 'ease-in', box('50% 0%'))}>
        <path
          d="M93 34 C97 40, 99 43, 96 46 C93 49, 88 47, 89 43 C90 40, 92 37, 93 34 Z"
          fill="var(--eds-semantic-color-status-info-500)"
        />
      </g>
      <g style={a('cfSweatR', 1.3, 'ease-in', { ...box('50% 0%'), animationDelay: d(-0.65) })}>
        <path
          d="M151 38 C155 44, 157 47, 154 50 C151 53, 146 51, 147 47 C148 44, 150 41, 151 38 Z"
          fill="var(--eds-semantic-color-status-info-500)"
        />
      </g>
      <rect
        x="96"
        y="68"
        width="52"
        height="50"
        rx="13"
        fill="var(--eds-semantic-color-accent-primary-800)"
      />
      <g style={a('cfType', 0.28, 'ease-in-out', box('100% 0%'))}>
        <path
          d="M102 82 L86 112"
          stroke="var(--eds-semantic-color-accent-primary-800)"
          strokeWidth="10"
          strokeLinecap="round"
        />
        <circle
          cx="85"
          cy="113"
          r="6"
          fill="var(--eds-semantic-color-background-primary)"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="2.5"
        />
      </g>
      <g
        style={a('cfType', 0.28, 'ease-in-out', { ...box('0% 0%'), animationDirection: 'reverse' })}
      >
        <path
          d="M142 82 L158 112"
          stroke="var(--eds-semantic-color-accent-primary-800)"
          strokeWidth="10"
          strokeLinecap="round"
        />
        <circle
          cx="159"
          cy="113"
          r="6"
          fill="var(--eds-semantic-color-background-primary)"
          stroke="var(--eds-semantic-color-gray-900)"
          strokeWidth="2.5"
        />
      </g>
      <rect
        x="70"
        y="117"
        width="104"
        height="14"
        rx="4"
        fill="var(--eds-semantic-color-gray-200)"
        stroke="var(--eds-semantic-color-gray-300)"
        strokeWidth="2"
      />
      {keyDelays.map((delay, i) => (
        <rect
          key={i}
          x={78 + i * 18}
          y="122"
          width="12"
          height="4"
          rx="2"
          fill="var(--eds-semantic-color-accent-primary-800)"
          style={a('cfKey', 0.28, 'steps(2, end)', { animationDelay: d(delay) })}
        />
      ))}
      <g style={a('cfZip', 0.38, 'ease-in-out')}>
        <path
          d="M54 98 L66 98"
          stroke="var(--eds-semantic-color-gray-300)"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M50 108 L60 108"
          stroke="var(--eds-semantic-color-gray-300)"
          strokeWidth="3"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}
